import { IPage } from "@types";
import HomeView from "components/home/HomeView";
import Layout from "components/layouts/Layout";
import { APP_ORIGIN, BASE_URL } from "config/constants";
import fetcher from "lib/fetcher";
import { getAllProducts } from "lib/getAllProducts";
import { buildOrganizationGraph, ORGANIZATION_LOGO_PATH } from "lib/jsonLd";
import { getMenus } from "lib/getMenus";
import { getPage } from "lib/getPage";
import { setCacheControl } from "lib/misc";
import { isPresent, isSame } from "lib/utils";
import { GetServerSidePropsContext, InferGetServerSidePropsType } from "next";
import Head from "next/head";

import { ICategory, IMenuItem, IProduct } from "store/api";

export let getServerSideProps = async ({
  req,
  res,
}: GetServerSidePropsContext) => {
  // Stop from running on client
  let isFirstServerCall = isSame(req?.url?.indexOf("/_next/data/"), -1);

  const emptyProps = {
    seoPage: "",
    title: "",
    description: "",
    keywords: "",
    categoryListSSR: [] as ICategory[],
    productListSSR: {} as Record<string, IProduct[]>,
    menus: {} as Record<string, IMenuItem[]>,
  };

  if (!isFirstServerCall) return { props: emptyProps };

  let { data: categoryData } = await fetcher<{
    categories: ICategory[];
  }>({
    url: `${BASE_URL}/v1/categories/getall`,
    headers: { lng: "ru" },
  });

  if (categoryData?.categories.length === 0)
    return { props: emptyProps, notFound: true };

  setCacheControl(res);

  let { products } = await getAllProducts("ru");
  const slicedCategories = categoryData?.categories.slice(0, 3);
  let slicedProducts: Record<string, IProduct[]> = {};

  if (isPresent(slicedCategories))
    for (const c of slicedCategories) {
      slicedProducts = {
        ...slicedProducts,
        [c.id]: products[c.id]?.slice(0, 3),
      };
    }

  let menus = await getMenus("ru");

  let seoPage = await getPage("ru", "main-seo");

  return {
    props: {
      seoPage: seoPage.text ?? "",
      title: seoPage.meta.title ?? "",
      description: seoPage.meta.description ?? "",
      keywords: seoPage.meta.keywords ?? "",
      categoryListSSR: categoryData?.categories ?? [],
      productListSSR: slicedProducts ?? ({} as Record<string, IProduct[]>),
      menus,
    },
  };
};

export type HomePageProps = InferGetServerSidePropsType<
  typeof getServerSideProps
>;

// Запасные title/description — те же, что уходят в <title> (getLayout),
// чтобы og:-теги совпадали с ними и при пустых SSR-props.
const DEFAULT_TITLE = "Доставка канапе и фуршетных блюд - Moscowfood";
const DEFAULT_DESCRIPTION = "Доставка канапе и фуршетных блюд - Moscowfood по Москве";

// Organization + og:-теги главной (SEO, сентябрь 2026): только данные,
// которые уже есть на сайте, — см. buildOrganizationGraph в lib/jsonLd.ts.
const organizationJsonLd = JSON.stringify(buildOrganizationGraph());

let HomePage: IPage<HomePageProps> = (props) => (
  <>
    <Head>
      <meta property="og:type" content="website" />
      <meta property="og:title" content={props.title || DEFAULT_TITLE} />
      <meta
        property="og:description"
        content={props.description || DEFAULT_DESCRIPTION}
      />
      <meta property="og:url" content={`${APP_ORIGIN}/`} />
      <meta
        property="og:image"
        content={`${APP_ORIGIN}${ORGANIZATION_LOGO_PATH}`}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: organizationJsonLd }}
      />
    </Head>
    <HomeView {...props} />
  </>
);

HomePage.getLayout = (page: { props: HomePageProps }) => (
  <Layout
    title={page.props.title || DEFAULT_TITLE}
    description={page.props.description || DEFAULT_DESCRIPTION}
    keywords={
      page.props.keywords ||
      "канапе тарталетки рулетики доставка кетеринг фуршетные блюда"
    }
    menus={page.props.menus}
    index
    hideBreadcrumbs
  >
    {page}
  </Layout>
);

export default HomePage;
